import { useRef, useEffect, useState } from 'react'
import { HAND_CONNECTIONS, Results as MediapipeHandsResults } from '@mediapipe/hands'
import { Pose, Joints, Connections, JointWeights } from '../utils/pose'
import { useSettings } from '../context/SettingsContext'

interface PoseCanvasProps {
  width: number
  height: number
  poseData: Pose | null
  currentKeyposeIdx: number
  handsResults: MediapipeHandsResults | null
}
interface DrawSkeletonOptions {
  color?: [number, number, number, number]
  comparePose?: Pose | null
  weights?: JointWeights | null
  threshold?: number
  lineWidth?: number
  outlineWidth?: number
}

interface OnionPoseProps {
  poseData: Pose
  keyposeIdx: number
  timestamp: number
}

export const PoseCanvas = ({ width, height, poseData, currentKeyposeIdx, handsResults }: PoseCanvasProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [onionPose, setOnionPose] = useState<OnionPoseProps | null>(null)
  const { settings } = useSettings()

  useEffect(() => {
    if (!poseData)
      return

    if (!onionPose || currentKeyposeIdx != onionPose.keyposeIdx) {
      setOnionPose({
        poseData: poseData.clone(),
        keyposeIdx: currentKeyposeIdx,
        timestamp: performance.now(),
      })
    }
  }, [poseData, onionPose, currentKeyposeIdx])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas)
      return

    const ctx = canvas.getContext('2d')
    if (!ctx)
      return

    const width = canvas.width
    const height = canvas.height
    ctx.clearRect(0, 0, width, height)

    if (!poseData) {
      return
    }

    const line = (x1: number, y1: number, x2: number, y2: number) => {
      ctx.beginPath()
      ctx.moveTo(x1 * width, y1 * height)
      ctx.lineTo(x2 * width, y2 * height)
      ctx.stroke()
    }

    const drawSkeleton = (pose: Pose, options: DrawSkeletonOptions = {}) => {
      const {
        color = [255, 255, 255, 1],
        comparePose = null,
        weights = null,
        threshold,
        lineWidth = 28,
        outlineWidth = 4,
      } = options

      if (!pose) return
      // Collect and sort connections by z-depth
      const connections: Array<{
        p1: number,
        p2: number,
        x1: number,
        y1: number,
        x2: number,
        y2: number,
        z: number,
        conf: number
      }> = []

      // Draw main pose connections
      for (const [p1, p2] of Connections) {
        // Skip face, hands and feet landmarks
        if (p1 < 11 || p2 < 11 ||
            (p1 >= 17 && p1 <= 22) ||
            (p2 >= 17 && p2 <= 22) ||
            (p1 >= 29) || (p2 >= 29)) {
          continue
        }

        const keypoint1Conf = pose.conf(p1)
        const keypoint2Conf = pose.conf(p2)
        const conf = Math.min(keypoint1Conf, keypoint2Conf)
        if (threshold !== undefined && conf < threshold) {
          continue
        }

        const keypoint1 = pose.pos2d(p1)
        const keypoint2 = pose.pos2d(p2)
        const z = Math.min(pose.keypoint(p1).z, pose.keypoint(p2).z)
        // const z = Math.max(keypoint1.z, keypoint2.z)

        connections.push({
          p1,
          p2,
          x1: keypoint1.x,
          y1: keypoint1.y,
          x2: keypoint2.x,
          y2: keypoint2.y,
          z,
          conf,
        })
      }

      // Sort by z depth
      connections.sort((a, b) => b.z - a.z)

      // Draw connections in sorted order
      for (const conn of connections) {
        const t = conn.conf
        const r = 255 * (1 - t)
        const g = 255 * t
        const b = 0

        ctx.strokeStyle = `rgba(0, 0, 0, ${color[3]})`
        ctx.lineWidth = lineWidth + outlineWidth
        line(conn.x1, conn.y1, conn.x2, conn.y2)

        if (threshold !== undefined) {
          ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, 1)`
        } else {
          ctx.strokeStyle = `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${color[3]})`
        }

        ctx.lineWidth = lineWidth
        line(conn.x1, conn.y1, conn.x2, conn.y2)
      }

      // draw hands
      if (handsResults) {
        for (let i = 0; i < handsResults.multiHandLandmarks.length; i++) {
          const landmarks = handsResults.multiHandLandmarks[i]
          const handedness = handsResults.multiHandedness[i]
          for (const [p1, p2] of HAND_CONNECTIONS) {
            const keypoint1 = landmarks[p1]
            const keypoint2 = landmarks[p2]

            ctx.strokeStyle = handedness.label == 'Left' ? 'rgb(0, 0, 255)' : handedness.label == 'Right' ? 'rgb(255, 0, 0)' : 'rgb(200, 127, 127)'
            ctx.lineWidth = 5
            line(keypoint1.x, keypoint1.y, keypoint2.x, keypoint2.y)
          }
        }
      }

      // Draw comparison pose if provided
      if (comparePose && weights) {
        for (const [p1, p2] of Connections) {
          if (p1 < 11 || p2 < 11 ||
              (p1 >= 17 && p1 <= 22) ||
              (p2 >= 17 && p2 <= 22) ||
              (p1 >= 29) || (p2 >= 29)) {
            continue
          }

          const keypoint1 = pose.pos2d(p1)
          const keypoint2 = pose.pos2d(p2)

          // Compare joint angles
          for (const [jointKey, weight] of Object.entries(weights)) {
            const joint = Joints[jointKey]
            if (weight > 0 && joint.indexOf(p1) === 1) {
              const j1 = pose.jointAngles[jointKey]
              const j2 = comparePose.jointAngles[jointKey]
              const angle = j1.angle
              const compareAngle = j2.angle
              const diff = Math.abs(angle - compareAngle) / 180
              const diffPercent1 = Math.pow(diff, 0.25)

              // Alpha based on joint confidence
              const diffPercentA = 1 / (1 + Math.exp(-j1.conf)) // sigmoid

              const diffColor: [number, number, number, number] = [
                100 + 155 * diffPercent1,
                255 * (1 - diffPercent1),
                0,
                255 * diffPercentA,
              ]

              ctx.lineWidth = 30 * diffPercentA
              ctx.strokeStyle = `rgba(${diffColor[0]}, ${diffColor[1]}, ${diffColor[2]}, ${diffColor[3] / 255})`
              line(keypoint1.x, keypoint1.y, keypoint2.x, keypoint2.y)
            }
          }
        }
      }
    }

    // Draw camera pose (green)
    // TODO: use own drawing functions to skip face, hand and feet
    ctx.save()
    ctx.lineCap = 'round'
    // ctx.setTransform(width, 0, 0, height, 0, 0);
    // ctx.scale(canvas.width, canvas.height)

    // TODO: this should also be a settings option
    if (onionPose) {
      const delta = performance.now() - onionPose.timestamp
      const duration = 500
      if (delta < duration) {
        const color = [0, 230, 250, Math.pow(1 - delta / duration, 1.3)] as [number, number, number, number]
        drawSkeleton(onionPose.poseData, { color, lineWidth: 42 + delta / duration * 20, outlineWidth: 4 })
      }
    }

    if (settings.display_showStickFigure) {
      drawSkeleton(poseData, {
        // comparePose: poseResults.poseLandmarks,
        // weights: poseResults.poseLandmarks.weights
        threshold: 0.7,
      })
    }

    ctx.restore()
  }, [poseData, onionPose, settings.display_showStickFigure, handsResults])

  return (
    <canvas
      ref={canvasRef}
      key="pose-canvas"
      width={width}
      height={height}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        transform: 'scaleX(-1)', // Mirror the canvas horizontally
      }}
    />
  )
}
